import {
  Box,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import Helmet from "react-helmet";
import { DataLoader, EmptyComponent, HeaderComponent } from "../components";
import { CollectionService } from "../services";
import { selectCollections } from "../store/collection/collection.selector";
import { ImageViewModal } from "../modals";

const HomePageLazy = React.lazy(() => import("./home"));
const AggregationPageLazy = React.lazy(() => import("./aggregation"));
const ClusteringPageLazy = React.lazy(() => import("./cluster-aggregation"));
const ClusterFacetsPageLazy = React.lazy(() => import("./cluster-facets"));
const AdvanceClusterAggregationPageLazy = React.lazy(
  () => import("./advance-cluster-aggregation")
);
const AdvanceClusterFacetsPageLazy = React.lazy(
  () => import("./advance-cluster-facets")
);
const RecommendationPageLazy = React.lazy(() => import("./recommendation"));

const Root: React.FC = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const collections = useSelector(selectCollections);

  useEffect(() => {
    if (!collections.initialized) {
      const auth = CollectionService.getAuth();
      CollectionService.listCollections({
        apiKey: auth.api_key,
        username: auth.username,
      });
    }
  }, [collections]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} overflow="hidden">
      <Helmet title="Vector A.I. Playground" />
      <DataLoader
        loading={collections.loading}
        loadingHeight="100vh"
        loadingText="Please wait while..."
      >
        <HeaderComponent />
        <ImageViewModal />
        <Box
          display="flex"
          flexGrow={1}
          bgcolor={theme.palette.secondary.main}
          px={isLargeScreen ? 8 : 2}
          py={isLargeScreen ? 2 : 2}
          flexDirection="column"
          overflow="hidden"
        >
          <React.Suspense fallback={<EmptyComponent content="loading..." />}>
            <Switch>
              <Route
                exact
                path="/"
                render={(props) => <HomePageLazy {...props} />}
              />
              <Route
                exact
                path="/aggregation"
                render={(props) => <AggregationPageLazy {...props} />}
              />
              <Route
                exact
                path="/clustering-aggregation"
                render={(props) => <ClusteringPageLazy {...props} />}
              />
              <Route
                exact
                path="/cluster-facets"
                render={(props) => <ClusterFacetsPageLazy {...props} />}
              />
              <Route
                exact
                path="/advance-cluster-aggregation"
                render={(props) => <AdvanceClusterAggregationPageLazy {...props} />}
              />
              <Route
                exact
                path="/advance-cluster-facets"
                render={(props) => <AdvanceClusterFacetsPageLazy {...props} />}
              />
              <Route
                exact
                path="/recommendation"
                render={(props) => <RecommendationPageLazy {...props} />}
              />
              <Route render={() => <Redirect to="/" />} />
            </Switch>
          </React.Suspense>
        </Box>
        <Divider />
        <Box
          bgcolor={theme.palette.secondary.main}
          px={isLargeScreen ? 8 : 1}
          py={2}
        >
          <Typography
            color="textSecondary"
            variant={isLargeScreen ? "body2" : "caption"}
          >
            Vector AI is a framework designed to make the process of building
            production grade vector based applications as quickly and easily as
            possible. Create, store, manipulate, search and analyse vectors
            alongside json documents to power applications such as neural
            search, recommendations, personalisation, etc.
          </Typography>
        </Box>
      </DataLoader>
    </Box>
  );
};

export default Root;
